import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SectionHead from '../../components/common/SectionHead';
import Card from '../../components/common/Card';
import EmptyState from '../../components/common/EmptyState';
import Spinner from '../../components/common/Spinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import CopyLinkButton from '../../components/common/CopyLinkButton';
import SignupForm from './SignupForm';
import SignupDetailView from './SignupDetailView';
import signupsService from '../../api/services/signups.service';
import useContextStore, { selectCurrentContext, scopeOfContext } from '../../store/useContextStore';
import useSignupsStore, { signupLinkUrl, signupCounts, signupUnitLabel, fmtSpotsTotal } from '../../store/useSignupsStore';
import { showToast } from '../../store/useToastStore';
import { fmtDate } from '../../utils/formatters';
import { IconTrash } from '../../components/icons';
import { PATHS } from '../../routes/navConfig';

const PAGE_SIZE = 50;

/* Volunteer slot booking — replaces SignUpGenius. Its own module, reached from
   the Links hub the same way Donation Links and Reminders are. Four sub-views:
   the list (where the rows and their Send to parents / Copy link / Edit /
   Delete live), create, edit and detail. Arriving with state.view (the hub's
   Create Sign-Up tile, the dashboard, a calendar event) opens straight onto
   that sub-view; leaving one lands back on the list. */
export default function SignupsPage() {
  const ctx = useContextStore(selectCurrentContext);
  const { signups, signupsLoading, signupsTotal, signupsTotalPages, loadSignups, patchSignup, removeSignup } =
    useSignupsStore();
  const location = useLocation();
  const navigate = useNavigate();

  const networkId = ctx?.schoolId ? ctx.id : null;
  const scope = scopeOfContext(ctx);

  const [view, setView] = useState(() => location.state?.view || 'list');
  const [activeId, setActiveId] = useState(() => location.state?.signupId ?? null);
  const [editing, setEditing] = useState(() => location.state?.view === 'edit');
  /* a fresh key resets the form's fields whenever the target changes */
  const [formKey, setFormKey] = useState(0);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [networkId]);

  useEffect(() => {
    if (!networkId) return;
    loadSignups(networkId, { page, limit: PAGE_SIZE }, scope).catch((err) => showToast(err.message));
  }, [networkId, page, scope, loadSignups]);

  const scoped = signups.filter((s) => s.scope === scope);
  const active = signups.find((s) => s.id === activeId) || null;

  /* The list sits under Links — its back button lands there. */
  const goLinks = () => navigate(PATHS.LINKS);
  const goList = () => {
    setEditing(false);
    setView('list');
  };
  const goDetail = (id) => {
    if (id != null) setActiveId(id);
    setEditing(false);
    setView('detail');
  };
  const openCreate = () => {
    setEditing(false);
    setActiveId(null);
    setFormKey((k) => k + 1);
    setView('create');
  };
  const openEdit = (id) => {
    setActiveId(id);
    setEditing(true);
    setFormKey((k) => k + 1);
    setView('edit');
  };
  const refresh = () =>
    networkId ? loadSignups(networkId, { page, limit: PAGE_SIZE }, scope).catch((err) => showToast(err.message)) : undefined;

  /* Sign-ups follow the same rule as links: status true = parents can see it.
     "Send to parents" publishes, "Hide from parents" takes it back to draft. */
  const [statusBusyId, setStatusBusyId] = useState(null);
  const toggleStatus = async (s) => {
    setStatusBusyId(s.id);
    try {
      await signupsService.setStatus(s.id, !s.status);
      patchSignup(s.id, { status: !s.status });
      showToast(s.status ? 'Sign-up hidden from parents' : 'Sign-up sent to parents ✓');
    } catch (err) {
      showToast(err.message);
    } finally {
      setStatusBusyId(null);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const confirmDelete = async () => {
    const s = deleteTarget;
    if (!s) return;
    setDeleting(true);
    try {
      await signupsService.remove(s.id);
      removeSignup(s.id);
      setDeleteTarget(null);
      showToast('Sign-up deleted ✓');
      /* the page is now short a row — pull it again, stepping back a page when
         that was the last row on it */
      if (scoped.length === 1 && page > 1) setPage((p) => p - 1);
      else refresh();
    } catch (err) {
      showToast(err.message);
    } finally {
      setDeleting(false);
    }
  };

  /* Dates & spots at a glance, so a row says what it is without opening it. */
  const signupMeta = (s) => {
    const first = s.dates.map((d) => d.date).filter(Boolean).sort()[0];
    return [
      signupUnitLabel(s),
      fmtSpotsTotal(signupCounts(s).cap),
      first ? `from ${fmtDate(first)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };

  /* Edit/View can arrive from elsewhere before the list has loaded — hold the
     view back until the sign-up it names is actually in hand. */
  const pending = (
    <div className="sec on">
      <div className="su-view on">
        <Card>
          <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '18px 0', justifyContent: 'center' }}>
            {signupsLoading ? <><Spinner variant="green" /> Loading sign-up…</> : 'That sign-up is no longer available.'}
          </div>
          {!signupsLoading && (
            <div className="inline-form-actions">
              <button className="btn btn-ghost" onClick={goList}>← Back to Sign-Ups</button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );

  if (view === 'create' || view === 'edit') {
    if (editing && !active) return pending;
    return (
      <div className="sec on">
        <div className="su-view on">
          <SignupForm
            key={`${formKey}-${editing ? activeId : 'new'}`}
            editingSignup={editing ? active : null}
            onCancel={goList}
            onSaved={async (id, isNew) => {
              if (isNew) {
                /* saving never notifies anyone — Send to parents lives on the row */
                if (page !== 1) setPage(1);
                else await refresh();
                goList();
                return;
              }
              await refresh();
              goDetail(id);
            }}
          />
        </div>
      </div>
    );
  }

  if (view === 'detail') {
    if (!active) return pending;
    return (
      <div className="sec on">
        <div className="su-view on">
          <SignupDetailView
            signup={active}
            onBack={goList}
            onDeleted={async () => {
              await refresh();
              goList();
            }}
            onEdit={() => openEdit(active.id)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="sec on">
      <div className="su-view on">
        <SectionHead
          title="Sign-Ups"
          sub="Volunteer sign-ups for this class and the links parents use to claim a spot."
          actions={<button className="btn btn-ghost" onClick={goLinks}>← Back to Links</button>}
        />
        <Card
          title="Sign-Up Links"
          action={
            <span className="btn btn-primary btn-sm" onClick={openCreate}>
              + Add Sign-Up Link
            </span>
          }
        >
          <div>
            {signupsLoading && (
              <div className="hint" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
                <Spinner variant="green" /> Loading sign-ups…
              </div>
            )}
            {!signupsLoading && scoped.length === 0 && <EmptyState>No sign-ups yet.</EmptyState>}
            {!signupsLoading &&
              scoped.map((s) => (
                <div className="signup-card" key={s.id}>
                  {s.image ? (
                    <img className="thumb" src={s.image} alt="" />
                  ) : (
                    <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 22 }}>🙋</div>
                  )}
                  <div className="info" style={{ cursor: 'pointer' }} title="View this sign-up" onClick={() => goDetail(s.id)}>
                    <div className="t">{s.title}</div>
                    <div className="meta">{signupLinkUrl(s) || signupMeta(s)}</div>
                  </div>
                  <div className="actions">
                    {/* .signup-card .actions doesn't center its children the way
                        .ev-actions does — without this the chip stretches to the
                        full height of the buttons next to it */}
                    <span className={`ev-status ${s.status ? 'published' : 'draft'}`} style={{ alignSelf: 'center' }}>
                      {s.status ? 'Sent to parents' : 'Not sent yet'}
                    </span>
                    <CopyLinkButton url={signupLinkUrl(s)} />
                    <button
                      className={`btn btn-sm ${s.status ? 'btn-ghost' : 'btn-primary'}`}
                      disabled={statusBusyId === s.id}
                      title={
                        s.status ? 'Parents can see this sign-up — click to hide it' : 'Make this sign-up visible to parents'
                      }
                      onClick={() => toggleStatus(s)}
                    >
                      {statusBusyId === s.id ? 'Saving…' : s.status ? 'Hide from parents' : 'Send to parents'}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(s.id)}>
                      Edit
                    </button>
                    <div className="ev-trash" title="Delete sign-up" onClick={() => setDeleteTarget(s)}>
                      <IconTrash />
                    </div>
                  </div>
                </div>
              ))}
            {signupsTotalPages > 1 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  marginTop: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                }}
              >
                <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                  {signupsTotal} sign-up{signupsTotal === 1 ? '' : 's'} · Page {page} of {signupsTotalPages}
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={page <= 1 || signupsLoading}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ← Prev
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={page >= signupsTotalPages || signupsLoading}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Sign-Up"
        confirmLabel="Delete Sign-Up"
        busy={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      >
        {deleteTarget && (
          <>
            <div className="signup-card" style={{ marginBottom: 12 }}>
              {deleteTarget.image ? (
                <img className="thumb" src={deleteTarget.image} alt="" />
              ) : (
                <div className="thumb" style={{ display: 'grid', placeItems: 'center', fontSize: 22 }}>🙋</div>
              )}
              <div className="info">
                <div className="t">{deleteTarget.title}</div>
                <div className="meta">{signupMeta(deleteTarget)}</div>
              </div>
            </div>
            <p style={{ fontSize: 13, color: 'var(--body)', lineHeight: 1.6, margin: 0 }}>
              This removes the sign-up and stops its link from working — anyone who already has it will see nothing.
              This can't be undone.
            </p>
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}
